import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import useListHighlight from '../../../hooks/useListHighlight';
import Badge from './Badge';

/**
 * Shared dropdown picker used by venue selection, city selection, and any future
 * click-to-open pickers on the dashboard.
 *
 * options   [{ key: string, label: string, sublabel?: string, badges?: string[] }]
 * value     the selected key (string), or null
 * onChange  (key | null) => void
 * allLabel  if provided, inserts an "All …" row at the top that calls onChange(null)
 * width     Tailwind width class (default 'w-[240px]'); pass 'w-full' for form fields
 * variant   'filter' (bold label, beside ToggleButtons) or 'form' (regular label and slate border, like input-bordered)
 * defaultHighlightKey  option highlighted (and scrolled to) on open when nothing is selected
 */
function DropdownPicker({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  allLabel = null,
  width = 'w-[240px]',
  variant = 'filter',
  borderClass = variant === 'form' ? 'input-bordered' : 'border-black',
  defaultHighlightKey = null,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = e => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [open]);

  const flatOptions = allLabel ? [{ key: null, label: allLabel }, ...options] : options;
  const selected = options.find(o => String(o.key) === String(value));
  const triggerLabel = selected ? selected.label : (allLabel ?? placeholder);

  const { highlightedIndex, setHighlightedIndex, hoverHighlight, registerOption, handleArrowKey } = useListHighlight(flatOptions.length, open);

  const openAtSelected = () => {
    const i = flatOptions.findIndex(o => (o.key === null ? value == null : String(o.key) === String(value)));
    const fallback = defaultHighlightKey == null ? -1 : flatOptions.findIndex(o => String(o.key) === String(defaultHighlightKey));
    setOpen(true);
    setHighlightedIndex(i >= 0 ? i : Math.max(fallback, 0));
  };

  const choose = index => {
    const opt = flatOptions[index];
    if (!opt) return;
    onChange(opt.key);
    setOpen(false);
  };

  const handleKeyDown = e => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault();
        openAtSelected();
      }
      return;
    }

    if (handleArrowKey(e)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      choose(highlightedIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className={`${width} h-9 px-3 border ${borderClass} rounded-lg flex items-center justify-between gap-2 transition-colors select-none focus:outline-none ${
          disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-700 cursor-pointer'
        }`}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openAtSelected())}
        onKeyDown={handleKeyDown}
        onBlur={e => {
          if (!rootRef.current?.contains(e.relatedTarget)) setOpen(false);
        }}
      >
        <span className={`text-sm truncate ${variant === 'form' ? '' : 'font-semibold'}`}>{triggerLabel}</span>
        <ChevronDownIcon className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 right-0 bg-white shadow-lg rounded-xl z-10 mt-1 border border-slate-200 overflow-y-auto min-w-full max-h-60"
          onMouseDown={e => e.preventDefault()}
        >
          {flatOptions.length === 0 && (
            <p className="px-4 py-2 text-sm text-slate-400">No options available</p>
          )}
          {flatOptions.map((opt, index) => (
            <div
              key={opt.key ?? '__all__'}
              ref={registerOption(index)}
              className={`px-4 py-2 cursor-pointer text-sm ${index === highlightedIndex ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => hoverHighlight(index)}
              onClick={() => choose(index)}
            >
              {opt.badges?.length > 0 ? (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate">{opt.label}</span>
                  {opt.badges.map(b => <Badge key={b}>{b}</Badge>)}
                </div>
              ) : (
                opt.label
              )}
              {opt.sublabel && <p className="text-xs text-slate-400 truncate" title={opt.sublabel}>{opt.sublabel}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DropdownPicker;
